import { useEffect } from "react";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import {
    FormGrid, FormSection, FieldInput, FieldTextarea, FieldSelect, FieldSwitch,
    FieldTags, FieldRichText, FieldRepeater, useForm,
} from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { NOTEBOOK_ENTRY_CONSTANTS } from "../_MODULES/_API";
import { isDuplicateRecord } from "../../../../_UI/recordUniqueness";

export const NOTEBOOK_TYPES = [
    { value: "NOTE", label: "Nota" },
    { value: "IDEA", label: "Idea" },
    { value: "BRAINSTORM", label: "Lluvia de ideas" },
    { value: "PROJECT", label: "Proyecto / planificación" },
    { value: "MUSIC", label: "Música / composición" },
    { value: "LYRICS", label: "Letra de canción" },
    { value: "POEM", label: "Poema" },
    { value: "BOOK", label: "Libro" },
    { value: "NOVEL", label: "Novela" },
    { value: "CHAPTER", label: "Capítulo" },
    { value: "STORY", label: "Cuento / relato" },
    { value: "SCENE", label: "Escena" },
    { value: "CHARACTER", label: "Personaje" },
    { value: "WORLD_BUILDING", label: "Mundo / ambientación" },
    { value: "SCRIPT", label: "Guion" },
    { value: "ARTICLE", label: "Artículo" },
    { value: "ESSAY", label: "Ensayo" },
    { value: "RESEARCH", label: "Investigación" },
    { value: "REFERENCE", label: "Referencia / documentación" },
    { value: "TUTORIAL", label: "Tutorial / guía" },
    { value: "COURSE_NOTE", label: "Apunte de curso" },
    { value: "MEETING", label: "Reunión / minuta" },
    { value: "PLAN", label: "Plan" },
    { value: "CHECKLIST", label: "Lista / checklist" },
    { value: "JOURNAL", label: "Diario" },
    { value: "DREAM", label: "Sueño" },
    { value: "QUOTE", label: "Cita / frase" },
    { value: "LETTER", label: "Carta" },
    { value: "SPEECH", label: "Discurso" },
    { value: "RECIPE", label: "Receta" },
    { value: "OTHER", label: "Otro / personalizado" },
];
export const NOTEBOOK_STATUSES = [
    { value: "IDEA", label: "Idea" },
    { value: "DRAFT", label: "Borrador" },
    { value: "PLANNED", label: "Planificado" },
    { value: "IN_PROGRESS", label: "En progreso" },
    { value: "REVIEW", label: "En revisión" },
    { value: "REVISION", label: "Reescritura / corrección" },
    { value: "PAUSED", label: "Pausado" },
    { value: "FINISHED", label: "Terminado" },
    { value: "PUBLISHED", label: "Publicado / entregado" },
    { value: "REFERENCE", label: "Sólo referencia" },
    { value: "CANCELLED", label: "Cancelado" },
];
const EMPTY_LINK = { label: "", url: "" };
const EMPTY_CUSTOM = { label: "", value: "" };
const EMPTY = {
    title: "", subtitle: "", type: "NOTE", status: "DRAFT", collectionId: "",
    summary: "", content: "", language: "Español", author: "", tags: [], source: "",
    isFavorite: false, isPinned: false, wordGoal: "", referenceLinks: [], customFields: [],
    genre: "", mood: "", musicalKey: "", bpm: "", timeSignature: "", tuning: "", capo: "", versionLabel: "",
    bookTitle: "", chapterNumber: "", sectionName: "",
};
const clone = (rows = []) => (Array.isArray(rows) ? rows.map((row) => ({ ...row })) : []);

const entryPayload = (v) => ({
    title: v.title, subtitle: v.subtitle, type: v.type, status: v.status, summary: v.summary, content: v.content,
    language: v.language, author: v.author, tags: v.tags, source: v.source, wordGoal: v.wordGoal,
    referenceLinks: v.referenceLinks, customFields: v.customFields,
    genre: v.genre, mood: v.mood, musicalKey: v.musicalKey, bpm: v.bpm, timeSignature: v.timeSignature,
    tuning: v.tuning, capo: v.capo, versionLabel: v.versionLabel,
    bookTitle: v.bookTitle, chapterNumber: v.chapterNumber, sectionName: v.sectionName,
});

export const NotebookEntryFormModal = ({ isOpen, entry = null, collections = [], entries = [], defaultCollectionId = "", onClose, onSaved }) => {
    const editing = !!entry?.id;
    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({ isToastError: true, isToastSuccess: true, minTimeMutate: 200 });
    const FORM = useForm({
        initialValues: EMPTY, schema: {},
        onSubmit: async (values) => {
            if (!String(values.title || "").trim()) { FORM.setError("title", "Escribí un título", { focus: true }); return; }
            if (isDuplicateRecord({
                rows: entries, value: values.title, currentId: entry?.id,
                getValue: (row) => row?.title, scope: values.collectionId || "", getScope: (row) => row?.collectionId || "",
            })) {
                FORM.setError("title", "Ya existe un registro con ese título en esta colección", { focus: true }); return;
            }
            const data = { entry: entryPayload(values), collectionId: values.collectionId || null, isFavorite: !!values.isFavorite, isPinned: !!values.isPinned };
            const res = editing
                ? await actionMutate({ method: "PATCH", endpoint: NOTEBOOK_ENTRY_CONSTANTS.UPDATE, id: entry.id, data, successMessage: "Registro actualizado" })
                : await actionMutate({ method: "POST", endpoint: NOTEBOOK_ENTRY_CONSTANTS.CREATE, data, successMessage: "Registro guardado" });
            if (!res.success) { FORM.applyApiError(res); return; }

            // El backend puede devolver sólo parte del registro según la operación.
            // Construimos el snapshot completo con los valores recién guardados para que
            // la colección, el contador y la vista previa se actualicen en el mismo render.
            const serverRow = res.data && typeof res.data === "object" ? res.data : {};
            const textSnapshot = String(values.content || "")
                .replace(/<[^>]*>/g, " ")
                .replace(/&nbsp;/g, " ")
                .replace(/\s+/g, " ")
                .trim();
            const wordCount = textSnapshot ? textSnapshot.split(/\s+/).length : 0;
            const saved = {
                ...(entry || {}),
                ...serverRow,
                ...entryPayload(values),
                id: serverRow.id || entry?.id,
                collectionId: values.collectionId || null,
                isFavorite: !!values.isFavorite,
                isPinned: !!values.isPinned,
                wordCount: serverRow.wordCount ?? wordCount,
                characterCount: serverRow.characterCount ?? textSnapshot.length,
                readingMinutes: serverRow.readingMinutes ?? (wordCount ? Math.max(1, Math.ceil(wordCount / 220)) : 0),
                updatedAt: serverRow.updatedAt || new Date().toISOString(),
            };
            onSaved?.(saved, editing); onClose?.();
        },
    });

    useEffect(() => {
        if (!isOpen) return;
        FORM.reset(editing ? {
            ...EMPTY, ...entry, collectionId: entry.collectionId || "", tags: [...(entry.tags || [])],
            referenceLinks: clone(entry.referenceLinks), customFields: clone(entry.customFields),
            wordGoal: entry.wordGoal || "", isFavorite: !!entry.isFavorite, isPinned: !!entry.isPinned,
        } : { ...EMPTY, collectionId: defaultCollectionId || "" });
    }, [isOpen, entry, defaultCollectionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isOpen) return undefined;
        const handler = (event) => {
            if ((event.ctrlKey || event.metaKey) && String(event.key).toLowerCase() === "s") {
                event.preventDefault(); FORM.onSubmit();
            }
        };
        window.addEventListener("keydown", handler); return () => window.removeEventListener("keydown", handler);
    }, [isOpen, FORM]);

    const collectionOptions = [{ value: "", label: "Sin colección" }, ...collections.map((c) => ({ value: c.id, label: c.name }))];
    const music = ["MUSIC", "LYRICS"].includes(FORM.values.type);
    const book = ["BOOK", "CHAPTER"].includes(FORM.values.type);

    return <AppModal isOpen={isOpen} onClose={onClose} isDirty={FORM.isDirty} size="full"
        className="LStudio__notebook_entry_editor_modal"
        title={editing ? `Editar · ${entry?.title || "registro"}` : "Nuevo registro"}
        description="Workspace de escritura del Cuaderno · metadatos a la izquierda, contenido a la derecha · Ctrl+S guarda."
        footer={<>
            <AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton>
            <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading}>{editing ? "Guardar cambios" : "Guardar registro"}</AppButton>
        </>}>
        <div className="LStudio__notebook_entry_editor_workspace">
            <aside className="LStudio__notebook_entry_editor_meta">
                <div className="LStudio__notebook_flags">
                    <FieldSwitch id="notebook_pin" label="Fijado" text="Mantener arriba" isChecked={!!FORM.values.isPinned} onChange={(e) => FORM.setValue("isPinned", e.target.checked)} />
                    <FieldSwitch id="notebook_fav" label="Destacado" text="Marcar como favorito" isChecked={!!FORM.values.isFavorite} onChange={(e) => FORM.setValue("isFavorite", e.target.checked)} />
                </div>

                <FormSection title="Organización" columns={1}>
                    <FieldInput {...FORM.field("title")} label="Título" placeholder="Ej: Horizonte de papel" />
                    <FieldInput {...FORM.field("subtitle")} label="Subtítulo" placeholder="Versión, capítulo o aclaración" />
                    <FieldSelect {...FORM.field("type")} label="Tipo" options={NOTEBOOK_TYPES} />
                    <FieldSelect {...FORM.field("status")} label="Estado" options={NOTEBOOK_STATUSES} />
                    <FieldSelect {...FORM.field("collectionId")} label="Colección" options={collectionOptions} />
                    <FieldInput {...FORM.field("author")} label="Autor / responsable" />
                    <FieldInput {...FORM.field("language")} label="Idioma" />
                    <FieldInput {...FORM.field("wordGoal")} label="Meta de palabras" placeholder="Ej: 1500 palabras / sin límite" />
                    <FieldTags id="notebook_tags" label="Etiquetas" value={FORM.values.tags} onChange={(tags) => FORM.setValue("tags", tags)} maxTags={50} span={1} />
                    <FieldTextarea {...FORM.field("summary")} label="Resumen / sinopsis" rows={4} />
                </FormSection>

                {music && <FormSection title="Composición musical" description="Datos útiles para trabajar la canción sin salir del Cuaderno." columns={1}>
                    <FieldInput {...FORM.field("genre")} label="Género" placeholder="Ej: Rock, groove metal, acústico..." />
                    <FieldInput {...FORM.field("mood")} label="Clima / intención" placeholder="Ej: Triste, agresivo, reflexivo..." />
                    <FieldInput {...FORM.field("versionLabel")} label="Versión" placeholder="Ej: Versión A, revisión 2..." />
                    <FieldInput {...FORM.field("musicalKey")} label="Tonalidad" placeholder="Dm" />
                    <FieldInput {...FORM.field("bpm")} label="BPM" placeholder="Ej: 120 / 120-128 / libre" />
                    <FieldInput {...FORM.field("timeSignature")} label="Compás" placeholder="4/4" />
                    <FieldInput {...FORM.field("tuning")} label="Afinación" placeholder="Drop C" />
                    <FieldInput {...FORM.field("capo")} label="Capo" placeholder="Sin capo / traste 2" />
                    <FieldInput {...FORM.field("source")} label="Referencia / inspiración" />
                </FormSection>}

                {book && <FormSection title="Libro / capítulo" columns={1}>
                    <FieldInput {...FORM.field("bookTitle")} label="Libro / obra" />
                    <FieldInput {...FORM.field("chapterNumber")} label="Capítulo / número" />
                    <FieldInput {...FORM.field("sectionName")} label="Sección" />
                    <FieldInput {...FORM.field("source")} label="Fuente / referencia" />
                </FormSection>}

                {!music && !book && <FormSection title="Referencia" columns={1}>
                    <FieldInput {...FORM.field("source")} label="Fuente / contexto" />
                </FormSection>}

                <FormSection title="Referencias y campos propios" columns={1}>
                    <FieldRepeater id="notebook_links" label="Enlaces" rows={FORM.values.referenceLinks} span={1}
                        onChange={(rows) => FORM.setValue("referenceLinks", rows)} emptyRow={EMPTY_LINK} addText="Agregar enlace" maxRows={30}
                        renderRow={({ row, index, onSet }) => <FormGrid columns={1}>
                            <FieldInput id={`notebook_link_label_${index}`} label="Nombre" value={row.label || ""} onChange={(e) => onSet("label", e.target.value)} />
                            <FieldInput id={`notebook_link_url_${index}`} label="URL" value={row.url || ""} onChange={(e) => onSet("url", e.target.value)} />
                        </FormGrid>} />
                    <FieldRepeater id="notebook_custom" label="Campos personalizados" rows={FORM.values.customFields} span={1}
                        onChange={(rows) => FORM.setValue("customFields", rows)} emptyRow={EMPTY_CUSTOM} addText="Agregar campo" maxRows={40}
                        renderRow={({ row, index, onSet }) => <FormGrid columns={1}>
                            <FieldInput id={`notebook_custom_label_${index}`} label="Campo" value={row.label || ""} onChange={(e) => onSet("label", e.target.value)} />
                            <FieldInput id={`notebook_custom_value_${index}`} label="Valor" value={row.value || ""} onChange={(e) => onSet("value", e.target.value)} />
                        </FormGrid>} />
                </FormSection>
            </aside>

            <main className="LStudio__notebook_entry_editor_writing">
                <div className="LStudio__notebook_entry_editor_writing_head">
                    <div>
                        <strong>{FORM.values.title || "Sin título"}</strong>
                        <span>{NOTEBOOK_TYPES.find((item) => item.value === FORM.values.type)?.label || "Nota"} · {NOTEBOOK_STATUSES.find((item) => item.value === FORM.values.status)?.label || "Borrador"}</span>
                    </div>
                    <small>El texto queda dentro del registro y se guarda cifrado.</small>
                </div>
                <div className="LStudio__notebook_entry_editor_content">
                    <FieldRichText {...FORM.fieldValue("content", { emptyValue: "" })} label="Contenido" minHeight={360}
                        placeholder="Escribí acá letras, capítulos, ideas, notas, guiones o investigación..." />
                </div>
            </main>
        </div>
    </AppModal>;
};
